import type { UrlHeadCheckResult } from '../api/types';

const CHECK_TIMEOUT_MS = 12_000;
const CONCURRENCY = 6;

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  signal: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signal.addEventListener('abort', onAbort, { once: true });
  const timer = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener('abort', onAbort);
  }
}

/** یک URL را از مرورگر کاربر با HEAD (و در صورت نیاز GET محدود) بررسی می‌کند. */
export async function checkUrlHeadInBrowser(
  url: string,
  signal: AbortSignal,
): Promise<UrlHeadCheckResult> {
  try {
    new URL(url);
  } catch {
    return { url, status: null, ok: false, error: 'invalid' };
  }

  try {
    let response = await fetchWithTimeout(
      url,
      { method: 'HEAD', redirect: 'follow', cache: 'no-store' },
      signal,
    );

    if (response.status === 405 || response.status === 501) {
      response = await fetchWithTimeout(
        url,
        {
          method: 'GET',
          redirect: 'follow',
          cache: 'no-store',
          headers: { Range: 'bytes=0-0' },
        },
        signal,
      );
    }

    return {
      url,
      status: response.status,
      ok: response.status === 200,
    };
  } catch (err) {
    if (signal.aborted) {
      return { url, status: null, ok: false, error: 'timeout' };
    }
    if (isAbortError(err)) {
      return { url, status: null, ok: false, error: 'timeout' };
    }
    // CORS، شبکه، یا مسدودسازی مرورگر → کاندید fallback بک‌اند
    return { url, status: null, ok: false, error: 'network' };
  }
}

/** خطای مرورگر که معمولاً CORS است و باید از سرور دوباره چک شود. */
export function shouldFallbackHeadToServer(
  item: UrlHeadCheckResult,
): boolean {
  return item.error === 'network';
}

/**
 * بررسی موازی HEAD از مرورگر کاربر.
 * onResult برای هر نتیجهٔ قطعی صدا زده می‌شود؛ برای network معمولاً تا بعد از fallback صبر کنید.
 */
export async function runBrowserHeadChecks(
  urls: string[],
  options: {
    signal: AbortSignal;
    onResult?: (item: UrlHeadCheckResult) => void;
  },
): Promise<UrlHeadCheckResult[]> {
  const results: (UrlHeadCheckResult | undefined)[] = new Array(urls.length);
  let cursor = 0;

  const runWorker = async () => {
    while (!options.signal.aborted) {
      const index = cursor;
      cursor += 1;
      if (index >= urls.length) break;
      const item = await checkUrlHeadInBrowser(urls[index], options.signal);
      if (options.signal.aborted) break;
      results[index] = item;
      options.onResult?.(item);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, urls.length) }, () =>
      runWorker(),
    ),
  );

  return results.filter((item): item is UrlHeadCheckResult => item != null);
}
